import { ConfirmModal, If } from 'aa_common/front-end/components'
import { defaultTo, everyTrue, ifElse, someTrue } from 'aa_common/front-end/helpers'
import { MASTER_PAGES } from 'common/constants'
import { getTrimmedData, isAccountingJournalTarget, parseDataForSubmit, transformObjectWithPath } from 'common/helpers'
import { getNs } from 'common/i18n-config'
import { FORM_MODES } from 'common/models'
import { FixedAssetsService, PresenterPreFillDraftAssetResponse } from 'common/open-api'
import { getAxiosSilent } from 'common/open-api/helper'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { HorizontalLine } from 'components/atoms'
import { BasicForm } from 'components/molecules'
import {
  addApiErrorKey,
  checkAcquiredAtBelongToAssetCodeAutoGenerate,
} from 'components/organisms/assets/AssetForm/helper'
import { useActionsStatus } from 'containers/assets/asset-list-container/hooks/use-actions-status'
import { tagListSlice } from 'containers/master/tags/store'
import { unitsSlice } from 'containers/master/units/store'
import { get, isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { fetchMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/actions'
import { fetchAutoGenerateSettings } from 'store/settings/assetCode/actions'
import { selectAssetCodeAutoGenerateSettingListData } from 'store/settings/assetCode/selectors'
import { cleanupExcise, fetchExciseList } from 'store/settings/excise/actions'

import { ReasonBox } from '../components/ReasonBox'
import { useCorrection } from '../hooks/use-correction'
import ErrorComponent from './ErrorComponent'
import { FormData } from './FormData'
import { useCorrectionBlocked } from './hooks/use-correction-blocked'
import { useFormatInitialAsset } from './hooks/use-format-initial-asset'
import { StyledTooltip, TopMessage, Wrapper } from './styles'
import { getAssetSchema, resetGeneralValidationErrors } from './validations/asset-schema'

const { t, useNsReady } = getNs(['components/organisms/assets/asset-form', 'common', 'components/molecules/basic-form'])

type Props = {
  errors?: any[]
  loading?: boolean
  initialAssetData: any
  formMode?: FORM_MODES
  confirming?: boolean
  isTemporaryData?: boolean
  onSubmit: (values: any) => void
  onSaveTemp?: (key: string, data: any, extraKey?: string) => void
  onDelete?: (biid: string) => void
  showRequiredError?: boolean
  asset?: any
  isValidateAssetLoading?: boolean
  isDraftFA?: boolean
  isReadOnlyDraftFA?: boolean
  calculatedDraftFA?: PresenterPreFillDraftAssetResponse
  shouldDisableSubmit?: boolean
  btnSubmitText?: string
  onDeleteButtonClick?: () => void
  disableDelete?: boolean
  onAcquiredAtChange?: (acquiredAt: string) => void
}

const AssetForm: React.FC<Props> = ({
  errors,
  loading = false,
  initialAssetData,
  formMode = FORM_MODES.NEW,
  confirming = false,
  isValidateAssetLoading = false,
  isTemporaryData = false,
  onSubmit,
  onSaveTemp,
  onDelete,
  showRequiredError,
  asset,
  isDraftFA,
  isReadOnlyDraftFA,
  calculatedDraftFA,
  shouldDisableSubmit,
  btnSubmitText,
  onDeleteButtonClick,
  disableDelete,
  onAcquiredAtChange,
}) => {
  useNsReady()

  const dispatch = useDispatch()
  const { biid } = useParams<{ biid: string }>()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const { isEnable: isD80AcquisitionCostValidationEnabled } = useCheckFlag(
    FeatureFlagNames.FaD80AcquisitionCostValidation
  )
  const validationSchema = useMemo(
    () => getAssetSchema(!!isD80AcquisitionCostValidationEnabled),
    [isD80AcquisitionCostValidationEnabled]
  )
  const assetCodeAutoGenerateSettingListData = useSelector(selectAssetCodeAutoGenerateSettingListData, isEqual)

  const [isCorrectable, setCorrectable] = useState<boolean>(false)
  const isEditMode = formMode === FORM_MODES.EDIT
  const isCorrectionMode = formMode === FORM_MODES.CORRECTION
  const formattedAssetData = useFormatInitialAsset(initialAssetData)
  const { findActionsStatusByBiid, getActionsStatus } = useActionsStatus()

  useEffect(() => {
    const checkCorrectable = async () => {
      try {
        const response = await FixedAssetsService.getFixedAssetsByBiidIsCorrectable(
          formattedAssetData.biid,
          getAxiosSilent()
        )
        setCorrectable(Boolean(response?.data))
      } catch (err) {
        setCorrectable(false)
      }
    }

    getActionsStatus([biid])
    isEditMode && checkCorrectable()
  }, [])

  const currentSetting = useMemo(() => {
    const acquiredAt = get(asset, 'acquired_at')

    if (someTrue(!assetCodeAutoGenerateSettingListData, !acquiredAt)) return null
    return assetCodeAutoGenerateSettingListData.find(setting =>
      checkAcquiredAtBelongToAssetCodeAutoGenerate(setting, acquiredAt)
    )
  }, [assetCodeAutoGenerateSettingListData, asset])

  const handleSubmit = useCallback(
    (values: any) => {
      const parsedValues = parseDataForSubmit(getTrimmedData(values))
      onSubmit(parsedValues)
    },
    [onSubmit]
  )

  const handleCancelDelete = (): void => {
    setIsConfirmingDelete(false)
  }

  const handleDelete = (): void => {
    onDelete?.(get(formattedAssetData, 'biid'))
    setIsConfirmingDelete(false)
  }

  const renderTopMessage = (children: string | React.ReactNode) => <TopMessage>{children}</TopMessage>

  const handleReloadMasterOptions = (pageName: string) => {
    if (pageName === MASTER_PAGES.UNITS) {
      unitsSlice.setFilters({}, true)
    }
  }

  useEffect(() => {
    dispatch(fetchExciseList({ filters: { is_active: true } }))
    dispatch(fetchAutoGenerateSettings())
    dispatch(fetchMonthlyClosingCurrent())
    tagListSlice.setFilters({}, true)
    unitsSlice.setFilters({}, true)

    return function cleanup() {
      dispatch(cleanupExcise())
      resetGeneralValidationErrors()
    }
  }, [])

  const actionsStatusByBiid = findActionsStatusByBiid(biid)
  const adjusted = get(actionsStatusByBiid, 'adjusted')

  const { change_situation, transition_at, acquired_at } = defaultTo(formattedAssetData, {})
  const {
    isCorrectable: isCorrectableWithPermission,
    isCorrectionLinkHidden,
    isCorrectionBlockedByEventHistory,
    checkEventsAllowedForCorrection,
    checkDateWithMonthlyClosing,
  } = useCorrection()
  const isCorrectionBlockedByMonthlyClosing = !!transition_at && !checkDateWithMonthlyClosing(transition_at)
  const isCorrectionBlockedByAcquisitionMonthlyClosing = !!acquired_at && !checkDateWithMonthlyClosing(acquired_at)
  const isEventsAllowedForCorrection = checkEventsAllowedForCorrection(change_situation?.code)
  // useCorrection folds the event-history side in (その他変更 included once the flag is on); the current change
  // situation is a separate signal and adds 区分変更 and 部分減損, which never reach the history list (AAISSUES-20763).
  const isCorrectionBlockedByEvents = someTrue(isCorrectionBlockedByEventHistory, !isEventsAllowedForCorrection)
  const { correctionBlockedReason, isCorrectionBlocked, isCorrectionBlockedReasonEnabled } = useCorrectionBlocked({
    biid,
    isCorrectionMode,
    isCorrectionBlockedByEventHistory: isCorrectionBlockedByEvents,
    isCorrectionBlockedByMonthlyClosing,
    transitionAt: transition_at,
    isCorrectionBlockedByAcquisitionMonthlyClosing,
    acquiredAt: acquired_at,
    adjusted: !!adjusted,
    isDraftFA,
  })
  const isShowTopMessageWithoutCorrect = someTrue(
    !isCorrectableWithPermission,
    !isEventsAllowedForCorrection,
    isCorrectionLinkHidden,
    everyTrue(isCorrectableWithPermission, !isEventsAllowedForCorrection)
  )
  // Accelerated depreciation only greys the link out on the legacy path; with the flag on the asset list keeps its
  // correction button clickable and the correction screen explains the reason instead.
  const isCorrectionLinkDisabled = everyTrue(!!adjusted, !isCorrectionBlockedReasonEnabled)

  const isPartialImpair = useMemo(() => {
    const accountingLedger = formattedAssetData?.fixed_asset_ledgers?.find((ledger: any) =>
      isAccountingJournalTarget(ledger.ledger_setting)
    )
    return accountingLedger?.original_depreciation_method_code
  }, [formattedAssetData])

  const isShowTopMessageWhenImportPartial = everyTrue(
    isPartialImpair,
    isEditMode,
    !isCorrectable,
    !isShowTopMessageWithoutCorrect,
    !isDraftFA
  )

  const apiErrors = useMemo(() => addApiErrorKey(transformObjectWithPath(errors)), [errors])

  return (
    <>
      <If condition={!!correctionBlockedReason}>
        <ReasonBox
          message={correctionBlockedReason?.message}
          linkText={correctionBlockedReason?.linkText}
          href={correctionBlockedReason?.href}
          className="correction-blocked-reason"
        />
      </If>
      <Wrapper>
        <If
          condition={everyTrue(
            isEditMode,
            isShowTopMessageWithoutCorrect,
            !isCorrectable,
            !isDraftFA,
            !isCorrectionBlockedReasonEnabled
          )}
        >
          {renderTopMessage(t('msg_notice_for_change'))}
        </If>
        <If condition={everyTrue(isCorrectionMode, !isDraftFA)}>{renderTopMessage(t('correction_notice_msg'))}</If>
        <If condition={isShowTopMessageWhenImportPartial}>
          {renderTopMessage(t('cannot_correct_due_to_import_partial'))}
        </If>

        <If
          condition={everyTrue(
            isCorrectableWithPermission,
            isEditMode,
            !isDraftFA,
            someTrue(
              isCorrectionBlockedReasonEnabled,
              everyTrue(isCorrectable, !isCorrectionLinkHidden, !isShowTopMessageWithoutCorrect)
            )
          )}
        >
          {renderTopMessage(
            <>
              {t('correction_text_paragraph_1')}
              <StyledTooltip content={ifElse(isCorrectionLinkDisabled, t('tooltip_for_accelerated_depreciation'), '')}>
                <If condition={!isCorrectionLinkDisabled}>
                  <Link to={`/assets/${biid}/correction`}>{t('actions.correction')}</Link>
                </If>

                <If condition={isCorrectionLinkDisabled}>
                  <span className="adjusted">{t('actions.correction')}</span>
                </If>
              </StyledTooltip>
              {t('correction_text_paragraph_2')}
            </>
          )}
        </If>

        <BasicForm
          initialValues={formattedAssetData}
          onSubmit={handleSubmit}
          errors={apiErrors}
          loading={loading || isValidateAssetLoading}
          preventSubmitOnEnter
          validationSchema={validationSchema}
        >
          <ErrorComponent errors={errors} values={asset} currentSetting={currentSetting} />
          <If condition={formattedAssetData}>
            <FormData
              initialValues={formattedAssetData}
              formMode={formMode}
              isTemporaryData={isTemporaryData}
              onSaveTemp={onSaveTemp}
              onHandleReloadMasterOptions={handleReloadMasterOptions}
              showRequiredError={showRequiredError}
              onSubmit={handleSubmit}
              confirming={confirming}
              isDraftFA={isDraftFA}
              isReadOnlyDraftFA={isReadOnlyDraftFA}
              calculatedDraftFA={calculatedDraftFA}
              shouldDisableSubmit={someTrue(!!shouldDisableSubmit, isCorrectionBlocked)}
              btnSubmitText={btnSubmitText}
              onDeleteButtonClick={onDeleteButtonClick}
              disableDelete={disableDelete}
              onAcquiredAtChange={onAcquiredAtChange}
              disableAllFields={isCorrectionBlocked}
            />
          </If>
          <HorizontalLine style={{ width: '100%', backgroundColor: '#D4D8DD' }} />

          <div style={{ height: 30 }} />
        </BasicForm>
        <ConfirmModal
          message={t('messages.confirm_delete', { name: get(formattedAssetData, 'name') })}
          visible={isConfirmingDelete}
          onOK={handleDelete}
          onCancel={handleCancelDelete}
        />
      </Wrapper>
    </>
  )
}

export default AssetForm
